import BaseVisualizationService from '@configuratorware/configurator-frontendgui/App/Services/VisualizationService';
import { EventTypes as VisualizationEventTypes } from 'redhotmagma-visualization/Constants';
import { THREE } from 'redhotmagma-visualization/ExposedModules';
import { getChairNumber, isTableSet, tableTopHasFrame } from '../App/Reducers/Configurator/Selecctors';

class VisualizationService extends BaseVisualizationService {
  setComponent(component) {
    super.setComponent(component);

    // prevent looking "under"
    component.observable.on(VisualizationEventTypes.Initialized, () => {
      component.controls.maxPolarAngle = Math.PI * 0.5;

      component.controls.addEventListener('change', function () {
        if (this.minPanZ && this.target.z < this.minPanZ) {
          this.target.z = this.minPanZ;
          this.update();
        }
      });
    });

    component.defaultCameraPosition.set(0, 1, 2);

    const addLightsIfNeededOrig = component.addLightsIfNeeded;
    component.addLightsIfNeeded = function addLightsIfNeeded(scene) {
      this.fixSceneScaling();
      addLightsIfNeededOrig.call(this, scene);
      const radius = this.getSceneContainerBox().getBoundingSphere(new THREE.Sphere()).radius;
      const anisotropy = this.renderer.capabilities.getMaxAnisotropy();
      scene.traverse((object) => {
        if (object.isMesh) {
          object.castShadow = !object.name?.toLowerCase().includes('_castnoshadow');
          object.receiveShadow = !object.name?.toLowerCase().includes('_receivenoshadow');
          if (object.material.map) {
            object.material.map.anisotropy = anisotropy;
          }
        }
        if (object.isLight) {
          this.setLightShadow(object, radius, { shadowMapSize: 2048 });
        }
        if (object.name?.toLowerCase().startsWith('renderfirst_')) {
          object.renderOrder = -1;
          object.castShadow = false;
        }
      });
    };

    component.fixImportedScene = function () {
      // skip core implementation
    };

    component.fixSceneScaling = function () {
      this.sceneContainer.scale.set(1, 1, 1);
      this.sceneContainer.updateWorldMatrix(true, true);
      const box = getSceneContainerBox.call(this);
      const size = box.getSize(new THREE.Vector3()).length();
      if (size >= 1000) {
        const scaling = 0.001;
        this.sceneContainer.scale.set(scaling, scaling, scaling);
        this.sceneContainer.updateWorldMatrix(true, true);
      }
    };

    const { getSceneContainerBox } = component;
    component.getSceneContainerBox = function () {
      const box = getSceneContainerBox.call(this);
      const center = box.getCenter(new THREE.Vector3());
      box.min.sub(center).multiplyScalar(0.5).add(center);
      box.max.sub(center).multiplyScalar(0.5).add(center);
      return box;
    };

    // use the current default camera as default screenshot camera
    const { getScreenshotsSync } = component;
    component.getScreenshotsSync = function (...args) {
      const defaultCameraView = this.cameraViews.default;
      this.screenshotViews.default = {
        ...defaultCameraView,
        position: defaultCameraView.position.clone(),
      };
      return getScreenshotsSync.call(this, ...args);
    };
    this.registerScreenshotsGetter(component.getScreenshotsSync.bind(component));

    const { setLightShadow } = component;
    component.setLightShadow = function (light, d = 50, options = {}) {
      setLightShadow.call(this, light, d * 2, {
        shadowMapSize: 2048,
        ...options,
      });
      this.fixDirLightNearFar(light);
    };

    component.fixDirLightNearFar = function (dirLight) {
      const box = getSceneContainerBox.call(this);
      const sphere = box.getBoundingSphere(new THREE.Sphere());
      const radius = sphere.radius;
      const center = box.getCenter(new THREE.Vector3());
      const lightDistance = dirLight.getWorldPosition(new THREE.Vector3()).sub(center).length();
      dirLight.shadow.camera.near = lightDistance - radius;
      dirLight.shadow.camera.far = lightDistance + radius;
      // these values prevent the "shadow acne" effect
      dirLight.shadow.bias = -0.0001;
      dirLight.shadow.normalBias = 0.01;
      dirLight.shadow.camera.updateProjectionMatrix();
    };

    const getCommonTexturePath = (configuration) =>
      configuration?.visualizationData?.sceneData?.find(({ params }) => params?.commonTexturePath)?.params
        .commonTexturePath;

    component.createLoadingManager = function createLoadingManager(options = {}) {
      const { basePath = '', resourcePath = '/' } = options;

      const { onLoad, onProgress, onError } = this._loadingManager;
      const loadingManager = new THREE.LoadingManager(onLoad, onProgress, onError);
      loadingManager.setURLModifier((url) => {
        if (/^(data|blob):/.test(url)) {
          return url;
        }
        const fixedBasePath = basePath.replace(/\/$/, '');
        const commonTexturePath = getCommonTexturePath(this._configuration);
        const customResourcePath =
          commonTexturePath && /\.(jpg|png)$/.test(url) ? commonTexturePath : resourcePath;
        return url.indexOf('/') === 0
          ? `${fixedBasePath}${url}`
          : `${fixedBasePath}${customResourcePath}${url.replace('./', '')}`;
      });
      return loadingManager;
    };

    const { update } = component;
    component.update = function (configuration) {
      component._disableRender = true;
      return update
        .call(this, configuration)
        .then(() => {
          this.updateDynamicParts(component.sceneContainer);
          this.updateControls(configuration);
        })
        .finally(() => {
          component._disableRender = false;
          component.render();
        });
    };

    component.updateControls = function (configuration) {
      if (configuration && isTableSet(configuration.item)) {
        const box = getSceneContainerBox.call(this);
        component.controls.minPanZ = box.min.z * 0.7;
        component.controls.minAzimuthAngle = -Math.PI * 0.5;
        component.controls.maxAzimuthAngle = Math.PI * 0.5;
      } else {
        component.controls.minPanZ = 0;
        component.controls.minAzimuthAngle = -Infinity;
        component.controls.maxAzimuthAngle = Infinity;
      }
    };

    this.registerUpdateFunction(component.update.bind(component));

    function matchModelsByPath(scene, regexp) {
      return scene.children.filter((obj) => regexp.test(obj.userData.path));
    }

    function updateModelsArr(models, updateFn) {
      models.forEach(updateFn);
    }

    function getPivotPoints(root) {
      const pivotPoints = {};
      root.traverse((obj) => {
        const matches = obj.name?.toLowerCase().match(/^pivot_(chair_(\d+)|table_top)/);
        if (matches) {
          pivotPoints[matches[1]] = obj;
        }
      });
      return pivotPoints;
    }

    function fitsMaxPercentDeviation(value, baseValue, maxPercent) {
      return Math.abs(value - baseValue) <= (baseValue * maxPercent) / 100;
    }

    component.shouldFitCameraPosition = function () {
      this.sceneContainer.updateWorldMatrix(true, true);
      const box = this.getSceneContainerBox();
      const size = box.getSize(new THREE.Vector3());
      if (
        this._size &&
        fitsMaxPercentDeviation(size.x, this._size.x, 20) &&
        fitsMaxPercentDeviation(size.z, this._size.z, 20)
      ) {
        return false;
      }
      this._size = size;
      return true;
    };

    function findObjectsWithNameMatch(root, regexp) {
      const objects = [];
      root.traverse((obj) => obj.name && regexp.test(obj.name) && objects.push(obj));
      return objects;
    }

    component.updateDynamicParts = function (scene) {
      // apply it only for table_set configuration
      if (!isTableSet(this._configuration.item)) {
        return;
      }

      if (!scene?.children?.length) {
        console.log('Scene is empty, skipping update of dynamic parts');
        return;
      }

      const pivotPoints = getPivotPoints(scene);

      // clean up clones
      scene?.children.filter((object) => object.isClone).forEach((object) => object.parent.remove(object));

      const tableTopModels = matchModelsByPath(scene, /components\/table_top/);

      if (tableTopModels.length && pivotPoints['table_top']) {
        const pivotPoint = pivotPoints['table_top'];
        updateModelsArr(tableTopModels, (model) => {
          model.position.copy(pivotPoint.position);
          model.rotation.copy(pivotPoint.rotation);
        });
      }

      const chairModels = matchModelsByPath(scene, /components\/(chair|cover)_model/);

      // hide original models
      updateModelsArr(chairModels, (model) => {
        model.visible = false;
      });

      const chairNumber = getChairNumber(this._configuration);

      // create the clones
      for (let i = 0; i < chairNumber; i++) {
        const pivotPoint = pivotPoints[`chair_${i + 1}`];
        pivotPoint &&
          updateModelsArr(chairModels, (segment) => {
            const segmentCopy = segment.clone();
            segmentCopy.isClone = true;
            segmentCopy.visible = true;
            segmentCopy.position.copy(pivotPoint.position);
            segmentCopy.rotation.copy(pivotPoint.rotation);
            scene.add(segmentCopy);
          });
      }

      // show or hide frame objects
      const hasFrame = tableTopHasFrame(this._configuration);
      const tableStandModels = matchModelsByPath(scene, /components\/table_stand/);
      updateModelsArr(tableStandModels, (tableStand) =>
        findObjectsWithNameMatch(tableStand, /_frame$/).forEach((obj) => (obj.visible = hasFrame))
      );

      if (this.shouldFitCameraPosition()) {
        this.fitSceneToCamera();
        this.initCameraViews(this.getCameras(scene));
      }
    };

    const { setupSceneCameras } = component;
    component.setupSceneCameras = function (cameras, center) {
      setupSceneCameras.call(this, cameras, center);
      const defaultTarget = this.sceneContainer.getObjectByName('cameratarget');
      if (defaultTarget) {
        this.cameraViews.default.lookat = defaultTarget.getWorldPosition(new THREE.Vector3());
      }
    };

    const { fitSceneToCamera } = component;
    component.fitSceneToCamera = function () {
      fitSceneToCamera.call(this);
      this.controls.maxDistance = this.controls.minDistance * 4;
      this.camera.near = this.controls.minDistance * 0.05;
      this.camera.far = this.controls.maxDistance * 2;
      this.camera.updateProjectionMatrix();
      this.controls.update();
    };

    component.setupGround = function () {
      // skip creating ground (as it is included in the scene)
    };
  }
}

export default VisualizationService;
